import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useLocation, useNavigate, useParams } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  ChevronUp,
  Linkedin,
  Loader2,
  Mail,
  MapPin,
  Plus,
  Share2,
  Trash2,
  Twitter,
} from "lucide-react";
import { toast } from "sonner";
import {
  raiseBoardApi,
  type RaiseBoardBySlugResponse,
  type RaiseBoardComment,
  type RaiseBoardCommentOrigin,
} from "@/api/raise-board-api";
import { lookbookApi, type LookbookPublicResponse } from "@/api/lookbook-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getErrorMessage } from "@/lib/api-error";
import {
  getGuestName,
  getVisitorId,
  setGuestName,
} from "@/lib/lookbook-access";
import { getStageText, GICSIndustries, GICSSubIndustries } from "@/lib/utils";
import { CompanyAvatar } from "./company-avatar";
import {
  fundCover,
  PageContainer,
  PublicCover,
  RaiseBoardNav,
  RaiseBoardShell,
  RBTag,
  type PublicBrand,
} from "./rb-ui";
import {
  useAddToPipeline,
  useMyVotes,
  useToggleUpvote,
} from "./use-raise-board-actions";
import {
  resolveLookbookVisibility,
  SECTION_ANCHORS,
} from "./lookbook-visibility";
import { LookbookDataRoom } from "./lookbook-data-room";
import { LookbookReports } from "./lookbook-reports";
import { DealSection, LookbookSectionNav } from "./lookbook-section-nav";

type Application =
  RaiseBoardBySlugResponse["result"]["applications"][number];

function getCompany(application: Application) {
  const ac = application.applicant_company;
  if (!ac || typeof ac === "string") return null;
  return ac;
}

function toArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function pickList(
  appValue: string | string[] | undefined,
  companyValue: string | string[] | undefined
): string[] {
  const fromApp = toArray(appValue);
  return fromApp.length ? fromApp : toArray(companyValue);
}

function dealInfoStr(
  application: Application,
  fieldName: string
): string | undefined {
  const v = application.deal_info?.find(
    (f) => f.field_name === fieldName
  )?.field_value;
  if (typeof v === "string" && v.trim()) return v;
  if (Array.isArray(v) && v.length > 0) return v.filter(Boolean).join(", ");
  if (typeof v === "number") return String(v);
  return undefined;
}

function formatRaiseAmount(amount?: string, currency?: string) {
  if (!amount) return null;
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const ccy = currency?.trim();
  return ccy ? `${trimmed} ${ccy}` : trimmed;
}

function resolveRaise(application: Application): string | null {
  const amount =
    dealInfoStr(application, "attempting_to_raise") ||
    (application.attempting_to_raise ?? "").trim();
  const currency =
    dealInfoStr(application, "currency") || (application.currency ?? "").trim();
  return formatRaiseAmount(amount || undefined, currency || undefined);
}

// Readable industries string for the email request.
function resolveIndustries(application: Application): string {
  const company = getCompany(application);
  const subIndustriesField = application.deal_info?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  );
  if (subIndustriesField?.field_value) {
    const codes = Array.isArray(subIndustriesField.field_value)
      ? subIndustriesField.field_value
      : [subIndustriesField.field_value as string];
    const names = codes
      .map((c) => GICSSubIndustries.find((si) => si.code === String(c))?.name)
      .filter((n): n is string => !!n);
    if (names.length > 0) return names.join(", ");
  }
  const fallback = application.industry ?? company?.industry ?? [];
  return fallback.filter(Boolean).join(", ");
}

function uniq(values: string[]): string[] {
  return values.filter((v, i, arr) => arr.indexOf(v) === i);
}

/** The workspace board a deal was published on, as populated on the payload. */
function getSourceBoard(application: Application): {
  slug?: string;
  enabled?: boolean;
} {
  const p = application.pertains_to_opportunity;
  if (!p || typeof p === "string") return {};
  return { slug: p.portal_slug, enabled: p.raise_board_enabled };
}

function findApplication(
  data: { result?: { applications?: Application[] } } | undefined,
  companyId: string
): Application | null {
  const apps = data?.result?.applications ?? [];
  return (
    apps.find((a) => {
      const ac = a.applicant_company;
      const id = typeof ac === "string" ? ac : ac?._id;
      return String(id) === companyId;
    }) ?? null
  );
}

export default function RaiseBoardDealProfile() {
  // Serves both /rb/:slug/:companyId and /lb/:lookbookPublicId/:companyId — the same
  // profile, differing only in where its deal is read from and where "back"
  // goes. Exactly one of slug / lookbookPublicId is ever set.
  const { slug, lookbookPublicId, companyId } = useParams<{
    slug: string;
    lookbookPublicId: string;
    companyId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by the global board when it navigates here. Absent on a cold-loaded
  // shared link, which correctly falls back to the deal's own board.
  const originFrom = (location.state as { from?: string } | null)?.from;
  // A lookbook wins over origin state: someone who arrived through a private
  // link must land back in that lookbook, not on a public board they may not
  // even be able to see.
  const backTo = lookbookPublicId
    ? `/lb/${lookbookPublicId}`
    : originFrom ?? (slug ? `/rb/${slug}` : "/global");
  const backLabel = lookbookPublicId
    ? "Back to lookbook"
    : backTo === "/global"
      ? "Back to Raiseboard"
      : "Back to board";
  // Recorded on any comment left here. Best effort by design: the global board
  // links to /rb/:slug/:companyId like every other board, so a cold-loaded
  // shared link has no origin state and reads as the public board. It is a
  // label on a shared thread, not a permission, so a wrong guess costs nothing
  // but a badge.
  const commentOrigin: RaiseBoardCommentOrigin = lookbookPublicId
    ? "lookbook"
    : originFrom === "/global"
      ? "global_board"
      : "public_board";
  const { isSignedIn, user } = useUser();
  const [emailing, setEmailing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{ id: string; name: string } | null>(
    null
  );
  const [authReason, setAuthReason] = useState<string | null>(null);
  const isDesktop = useIsDesktop();

  const addToPipeline = useAddToPipeline();

  // Both endpoints return `result.applications`, so the deal is found the same
  // way either way. The lookbook payload additionally carries the lookbook's
  // own upvote and comment counts in place of the public ones.
  // Named explicitly: the two branches return different shapes, and without the
  // union TS picks the first overload and can't assign the other one.
  const { data, isLoading, error } = useQuery<
    LookbookPublicResponse | RaiseBoardBySlugResponse
  >({
    queryKey: lookbookPublicId ? ["lookbook", lookbookPublicId] : ["raise-board", slug],
    queryFn: () =>
      lookbookPublicId
        ? lookbookApi.getByPublicId(lookbookPublicId)
        : raiseBoardApi.getBySlug(slug as string),
    enabled: !!slug || !!lookbookPublicId,
    retry: false,
  });

  const application = findApplication(data, companyId ?? "");

  // Signed-out visitors vote and comment as guests on a lookbook whose owner
  // allows it. Only a lookbook payload carries the switch, so /rb/ never does.
  const guest =
    !isSignedIn &&
    !!lookbookPublicId &&
    (data as LookbookPublicResponse | undefined)?.result?.lookbook
      ?.guest_engagement === true;
  const votedIds = useMyVotes(!!isSignedIn || guest, lookbookPublicId, guest);
  const toggleVote = useToggleUpvote({ slug, lookbookPublicId, guest });

  // Absent on /rb/ and /global, and on any lookbook entry added before this
  // existed — all of which resolve to profile-only, i.e. today's page.
  const show = resolveLookbookVisibility(application?.sharing);

  // The one-pager email is addressed by the deal's own board slug, which a
  // lookbook URL doesn't carry — read it off the deal instead. The endpoint
  // also requires that board to be publicly enabled, so a lookbook drawn from
  // a workspace with its board switched off hides the action rather than
  // offering a button that can only fail.
  const source = application ? getSourceBoard(application) : null;
  const emailSlug = slug ?? source?.slug;
  const canEmail = Boolean(emailSlug && (slug || source?.enabled));

  const sendEmail = useMutation({
    mutationFn: async (email: string) => {
      if (!emailSlug || !application) throw new Error("Missing deal");
      return raiseBoardApi.sendDealEmail({
        slug: emailSlug,
        applicationId: String(application._id),
        recipientEmail: email,
        industries: resolveIndustries(application),
      });
    },
    onSuccess: (_data, email) => {
      toast.success(`Deal details sent to ${email}.`);
      setEmailing(false);
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, "Failed to send deal details"));
    },
  });

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };

  const applicationId = application ? String(application._id) : "";
  const voted = votedIds.has(applicationId);
  const voteCount = application?.upvote_count ?? 0;

  const handleVote = () => {
    if (!isSignedIn && !guest) {
      setAuthReason("Sign in to upvote this deal.");
      return;
    }
    if (applicationId) toggleVote.mutate(applicationId);
  };

  const handleAddToPipeline = () => {
    if (!application) return;
    const company = getCompany(application);
    const ac = application.applicant_company;
    const id = typeof ac === "string" ? ac : ac?._id;
    if (!id) return;
    setPendingAdd({
      id: String(id),
      name: company?.company_name ?? "This company",
    });
  };

  // The fund owns the page, the same as on the lookbook or board this deal was
  // opened from: its mark leads the nav and takes you back there.
  const owner = data?.result?.opportunity;
  const ownerName = owner?.opportunity_name;
  const brand: PublicBrand | undefined = ownerName
    ? {
        name: ownerName,
        logoUrl: (owner as { logo_url?: string } | undefined)?.logo_url,
        href: lookbookPublicId ? `/lb/${lookbookPublicId}` : slug ? `/rb/${slug}` : undefined,
        context: lookbookPublicId ? "Lookbook" : "Raiseboard",
      }
    : undefined;

  // Where this deal sits, for the cover's eyebrow: the lookbook it was shared
  // in, or the board it was published on.
  const lookbookName = lookbookPublicId
    ? (data as LookbookPublicResponse | undefined)?.result?.lookbook?.name
    : undefined;
  const placeLabel = lookbookName ?? ownerName;

  const cover = fundCover(owner);
  const eyebrow = (
    <>
      <BackLink label={backLabel} onClick={() => navigate(backTo)} />
      {application && (
        <>
          <span aria-hidden className="hidden text-muted-foreground/50 sm:inline">
            ·
          </span>
          <RaisingMark />
          {placeLabel && (
            <span className="normal-case tracking-[0.02em]">In {placeLabel}</span>
          )}
        </>
      )}
    </>
  );

  const rail = application ? (
    <EngagementRail
      application={application}
      voted={voted}
      voteCount={voteCount}
      onVote={handleVote}
      copied={copied}
      onShare={handleShare}
      onEmail={() => setEmailing(true)}
      canEmail={canEmail}
      onAddToPipeline={handleAddToPipeline}
      comments={
        isDesktop ? (
          <CommentsPanel
            id="lb-comments"
            applicationId={String(application._id)}
            lookbookPublicId={lookbookPublicId}
            origin={commentOrigin}
            guest={guest}
            scroll
          />
        ) : undefined
      }
    />
  ) : null;

  return (
    <RaiseBoardShell
      footerNote={
        lookbookPublicId
          ? ownerName
            ? `Shared privately by ${ownerName}. Please keep this link to yourself.`
            : "Shared privately. Please keep this link to yourself."
          : undefined
      }
    >
      <RaiseBoardNav onRequestAuth={setAuthReason} brand={brand} background={cover} />

      {isLoading ? (
        <DealSkeleton />
      ) : error || !application ? (
        <PublicCover
          size="lg"
          background={cover}
          eyebrow={eyebrow}
          title="This deal isn't available"
          lede="It may have been taken off the board, or the link is out of date."
          aside={
            <Button
              className="h-10 rounded-lg px-4 text-[13.5px]"
              onClick={() => navigate(backTo)}
            >
              <ArrowLeft className="h-4 w-4" />
              {backLabel}
            </Button>
          }
        />
      ) : (
        <>
          <DealCover application={application} eyebrow={eyebrow} background={cover} />

          <PageContainer className="pb-4 pt-8 sm:pt-10">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_21rem] lg:gap-12">
              {/* The engagement rail: everything a visitor can do about the deal,
                  kept in reach while they read. On mobile the round and the
                  actions come first, and the thread closes the page. */}
              <aside className="min-w-0 lg:col-start-2 lg:row-start-1">
                <div className="lg:sticky lg:top-20">{rail}</div>
              </aside>

              <div className="min-w-0 lg:col-start-1 lg:row-start-1">
                <LookbookSectionNav sections={show.sections} />

                <DealAbout application={application} />

                {show.profile && (
                  <DealNarrative
                    application={application}
                    id={SECTION_ANCHORS.profile}
                  />
                )}

                {show.sections.includes("data-room") && lookbookPublicId && (
                  <LookbookDataRoom
                    id={SECTION_ANCHORS["data-room"]}
                    files={show.files}
                    publicId={lookbookPublicId}
                    companyId={companyId as string}
                  />
                )}

                {show.sections.includes("reports") && lookbookPublicId && (
                  <LookbookReports
                    id={SECTION_ANCHORS.reports}
                    reportKeys={show.reportKeys}
                    publicId={lookbookPublicId}
                    companyId={companyId as string}
                  />
                )}

                {!isDesktop && (
                  <div className="mt-12 border-t border-foreground pt-6">
                    <CommentsPanel
                      id="lb-comments"
                      applicationId={String(application._id)}
                      lookbookPublicId={lookbookPublicId}
                      origin={commentOrigin}
                      guest={guest}
                    />
                  </div>
                )}
              </div>
            </div>
          </PageContainer>
        </>
      )}

      <SendEmailDialog
        open={emailing}
        application={application}
        defaultEmail={user?.primaryEmailAddress?.emailAddress ?? ""}
        isSending={sendEmail.isPending}
        onSend={(email) => sendEmail.mutate(email)}
        onClose={() => setEmailing(false)}
      />

      <AlertDialog
        open={!!pendingAdd}
        onOpenChange={(open) => {
          if (!open) setPendingAdd(null);
        }}
      >
        <AlertDialogContent>
          {isSignedIn ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Add this company to your pipeline?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <span className="font-medium">{pendingAdd?.name}</span> will be
                  added as a deal in your DiligenceGPT pipeline, where you can
                  review it and run due diligence.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    if (pendingAdd) addToPipeline.mutate(pendingAdd.id);
                    setPendingAdd(null);
                  }}
                >
                  Add to Pipeline
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Add this company to your pipeline
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Adding <span className="font-medium">{pendingAdd?.name}</span>{" "}
                  to your pipeline requires a DiligenceGPT account. Get started
                  to review this deal and run your own due diligence.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="sm:justify-between">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setPendingAdd(null);
                    setAuthReason("Sign in to add this deal to your pipeline.");
                  }}
                >
                  Get started
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      <BoardAuthDialog
        open={!!authReason}
        onOpenChange={(open) => {
          if (!open) setAuthReason(null);
        }}
        reason={authReason ?? undefined}
      />
    </RaiseBoardShell>
  );
}

/**
 * Every derived display value for one deal, resolved once so the identity
 * card and the sidebar can't disagree about what the deal says.
 */
function useDealDetails(application: Application) {
  const company = getCompany(application);

  // Every approved GICS code the deal carries, resolved to both its
  // sub-industry name and its parent industry. All of them, not just the
  // first — a company tagged with three sub-industries should show three.
  const subIndustriesField = application.deal_info?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  );
  const codes: string[] = subIndustriesField?.field_value
    ? (Array.isArray(subIndustriesField.field_value)
        ? subIndustriesField.field_value
        : [subIndustriesField.field_value]
      )
        .map(String)
        .filter(Boolean)
    : [];

  const subIndustries = uniq(
    codes
      .map((c) => GICSSubIndustries.find((si) => si.code === c)?.name)
      .filter((n): n is string => !!n)
  );
  let industries = uniq(
    codes
      .map((c) =>
        c.length >= 8
          ? GICSIndustries.find((i) => i.code === c.substring(0, 8))?.name
          : undefined
      )
      .filter((n): n is string => !!n)
  );
  if (industries.length === 0) {
    industries = (application.industry ?? company?.industry ?? []).filter(
      Boolean
    );
  }

  const dealInfoCountry = application.deal_info?.find(
    (f) => f.field_name === "address"
  )?.field_value as string | undefined;

  const stageValue = application.stage ?? company?.stage;
  const stageLabel = stageValue != null ? getStageText(stageValue) : null;

  const website = company?.company_website ?? application.company_website;

  const source = (() => {
    const p = application.pertains_to_opportunity;
    if (!p || typeof p === "string") return {};
    return {
      name: p.opportunity_name,
      slug: p.portal_slug,
      enabled: p.raise_board_enabled,
    };
  })();

  return {
    company,
    name: company?.company_name ?? "Untitled",
    bio: company?.short_bio ?? application.short_bio,
    country: dealInfoCountry || company?.country || application.country,
    industries,
    subIndustries,
    stageLabel: stageLabel && stageLabel !== "Unknown" ? stageLabel : null,
    raise: resolveRaise(application),
    businessModels: pickList(application.business_model, company?.business_model),
    revenueModels: pickList(application.revenue_model, company?.revenue_model),
    founded: application.year_of_inception,
    website,
    websiteHost: website
      ? website.replace(/^https?:\/\//, "").replace(/\/$/, "")
      : null,
    websiteHref: website
      ? website.startsWith("http")
        ? website
        : `https://${website}`
      : null,
    linkedin: application.linkedIn,
    twitter: application.twitter,
    source,
  };
}

const DESKTOP_QUERY = "(min-width: 1024px)";

/**
 * Whether the two-column layout is showing. Read on first render, so a
 * desktop visitor never sees the mobile order flash before it settles.
 */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(DESKTOP_QUERY).matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(DESKTOP_QUERY);
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return isDesktop;
}

function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex items-center gap-1.5 rounded-md normal-case tracking-normal text-muted-foreground transition-colors duration-150 hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-150 group-hover:-translate-x-0.5" />
      {label}
    </button>
  );
}

/** "Raising": the page's one green signal. */
function RaisingMark() {
  return (
    <span className="inline-flex items-center gap-2 text-raising">
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inset-0 rounded-full bg-raising/40 motion-safe:animate-ping [animation-duration:2.4s]" />
        <span className="relative h-1.5 w-1.5 rounded-full bg-raising" />
      </span>
      Raising
    </span>
  );
}

/**
 * The cover: who the company is, in one compact band. The logo leads, the
 * name is the title, and the one-liner and quiet facts sit beneath it. Every
 * action lives in the engagement rail instead.
 *
 * Identity is always rendered, even when a lookbook hides the profile. A page
 * that opens on a bare file list with no company name is unusable, and all of
 * it was already on the row the visitor clicked to get here, so hiding it
 * would cost usability and protect nothing.
 */
function DealCover({
  application,
  eyebrow,
  background,
}: {
  application: Application;
  eyebrow: ReactNode;
  background: ReturnType<typeof fundCover>;
}) {
  const d = useDealDetails(application);

  const links: Array<{ label: string; href: string; icon: typeof Linkedin }> =
    [];
  if (d.linkedin)
    links.push({
      label: "LinkedIn",
      href: d.linkedin.startsWith("http") ? d.linkedin : `https://${d.linkedin}`,
      icon: Linkedin,
    });
  if (d.twitter)
    links.push({
      label: "Twitter",
      href: d.twitter.startsWith("http") ? d.twitter : `https://${d.twitter}`,
      icon: Twitter,
    });

  const facts = [
    d.stageLabel,
    d.country && (
      <span className="inline-flex items-center gap-1">
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        {d.country}
      </span>
    ),
    d.founded && `Founded ${d.founded}`,
  ].filter(Boolean);

  return (
    <PublicCover
      size="lg"
      background={background}
      eyebrow={eyebrow}
      leading={
        <CompanyAvatar
          logoUrl={d.company?.logo_url}
          name={d.company?.company_name}
          size="xl"
          tone="soft"
        />
      }
      title={<span className="break-words">{d.name}</span>}
      lede={
        <>
          {d.bio && <p className="line-clamp-2">{d.bio}</p>}
          {/* Spaced, not dot-separated, so a wrapped line never starts on a
              separator. */}
          {(facts.length > 0 || d.websiteHref || links.length > 0) && (
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-muted-foreground">
              {facts.map((fact, i) => (
                <span key={i}>{fact}</span>
              ))}
              {d.websiteHref && d.websiteHost && (
                <a
                  href={d.websiteHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {d.websiteHost}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              )}
              {links.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="-m-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-secondary hover:text-foreground"
                >
                  <Icon className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          )}
        </>
      }
    />
  );
}

/**
 * The engagement rail: the round at a glance, the endorsement, the things a
 * visitor can do with the deal, and (on desktop) the conversation. One quiet
 * panel with hairline sections, sticky beside the content as it scrolls.
 */
function EngagementRail({
  application,
  voted,
  voteCount,
  onVote,
  copied,
  onShare,
  onEmail,
  canEmail,
  onAddToPipeline,
  comments,
}: {
  application: Application;
  voted: boolean;
  voteCount: number;
  onVote: () => void;
  copied: boolean;
  onShare: () => void;
  onEmail: () => void;
  /** False when the deal's own board isn't public, so the email can't be sent. */
  canEmail: boolean;
  onAddToPipeline: () => void;
  /** The thread, when the rail carries it (desktop). */
  comments?: ReactNode;
}) {
  const d = useDealDetails(application);
  const action =
    "flex h-[3.25rem] flex-col items-center justify-center gap-1 rounded-lg border border-border bg-background px-1 text-center text-[12px] font-medium leading-tight text-foreground/80 transition-colors duration-150 hover:border-foreground/25 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30";

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card motion-safe:animate-rise lg:max-h-[calc(100dvh-6.5rem)]">
      <div className="shrink-0 px-5 pb-4 pt-5">
        <p className="text-[11.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          The round
        </p>
        {d.raise ? (
          <p className="mt-1.5 text-[1.75rem] font-semibold leading-none tabular-nums tracking-[-0.03em]">
            {d.raise}
          </p>
        ) : (
          <p className="mt-1.5 text-[16px] font-medium text-foreground/80">
            Raise not disclosed
          </p>
        )}
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          {[d.stageLabel, d.source.name && `via ${d.source.name}`]
            .filter(Boolean)
            .join(", ") || "\u00a0"}
        </p>
      </div>

      <div className="shrink-0 space-y-2.5 border-t border-border/80 p-5">
        {/* Endorsing is the one thing a visitor comes here to do, so it is the
            loudest control on the page. */}
        <button
          type="button"
          onClick={onVote}
          aria-pressed={voted}
          aria-label={voted ? "Remove endorsement" : "Endorse this deal"}
          className={cn(
            "flex h-12 w-full items-center justify-between rounded-lg px-4 text-[14.5px] font-semibold transition-[background-color,border-color,color,transform] duration-150 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30",
            voted
              ? "border border-foreground/25 bg-background text-foreground hover:border-foreground/40"
              : "bg-foreground text-background hover:bg-foreground/90"
          )}
        >
          <span className="inline-flex items-center gap-2">
            {voted ? <Check className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" strokeWidth={2.5} />}
            {voted ? "Endorsed" : "Endorse this deal"}
          </span>
          <span className="text-[13px] font-medium tabular-nums opacity-75">
            {voteCount} {voteCount === 1 ? "endorsement" : "endorsements"}
          </span>
        </button>

        <div className={cn("grid gap-2", canEmail ? "grid-cols-3" : "grid-cols-2")}>
          <button
            type="button"
            className={action}
            onClick={onShare}
            aria-label={copied ? "Link copied" : "Copy share link"}
          >
            {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
            {copied ? "Copied" : "Share"}
          </button>
          {canEmail && (
            <button type="button" className={action} onClick={onEmail}>
              <Mail className="h-4 w-4" />
              Email me this
            </button>
          )}
          <button type="button" className={action} onClick={onAddToPipeline}>
            <Plus className="h-4 w-4" />
            Add to pipeline
          </button>
        </div>
      </div>

      {comments && (
        <div className="flex min-h-0 flex-1 flex-col border-t border-border/80">
          {comments}
        </div>
      )}
    </div>
  );
}

/**
 * About: the full one-liner (the cover clamps it) and every classification
 * the deal carries. Identity, so it shows whatever the lookbook shares.
 */
function DealAbout({ application }: { application: Application }) {
  const d = useDealDetails(application);
  const tags = uniq(
    [...d.industries, ...d.subIndustries, ...d.businessModels].filter(Boolean)
  );
  if (!d.bio && tags.length === 0) return null;
  return (
    <DealSection title="About" className="pt-0 sm:pt-0">
      <div className="py-6">
        {d.bio && <Prose>{d.bio}</Prose>}
        {tags.length > 0 && <TagRun tags={tags} />}
      </div>
    </DealSection>
  );
}

/** The page's shape while the deal loads, so nothing jumps when it lands. */
function DealSkeleton() {
  return (
    <div role="status" aria-label="Loading deal">
      <PublicCover
        size="lg"
        eyebrow={<Skeleton className="h-3 w-28" />}
        leading={<Skeleton className="h-14 w-14 rounded-xl" />}
        title={<Skeleton className="h-10 w-64 max-w-full rounded-lg" />}
        lede={<Skeleton className="h-4 w-full max-w-[48ch]" />}
      />
      <PageContainer className="pt-8 sm:pt-10">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_21rem] lg:gap-12">
          <div className="space-y-3 lg:col-start-1 lg:row-start-1">
            <Skeleton className="h-7 w-32" />
            <div className="space-y-3 border-t border-border pt-6">
              <Skeleton className="h-4 w-full max-w-[60ch]" />
              <Skeleton className="h-4 w-full max-w-[56ch]" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
          <div className="space-y-3 rounded-xl border border-border p-5 lg:col-start-2 lg:row-start-1">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-12 w-full rounded-lg" />
            <Skeleton className="h-[3.25rem] w-full rounded-lg" />
          </div>
        </div>
      </PageContainer>
    </div>
  );
}

/**
 * The pitch: the round, then problem / solution / traction, then the facts.
 *
 * Read like a memo rather than a stack of cards: one section under an ink
 * rule, each part a row on a hairline, its label in a narrow left column on
 * desktop, the prose held to a readable measure.
 *
 * This, not the cover, is what a lookbook's "Profile" switch turns off. The
 * server strips these fields from the payload when it is off, so this renders
 * nothing on its own; the flag is belt and braces.
 */
function DealNarrative({
  application,
  id,
}: {
  application: Application;
  id?: string;
}) {
  const d = useDealDetails(application);

  const pitchSections: Array<{ label: string; body: string }> = [];
  if (application.problem)
    pitchSections.push({ label: "Problem", body: application.problem });
  if (application.solution)
    pitchSections.push({ label: "Solution", body: application.solution });
  if (application.traction)
    pitchSections.push({ label: "Traction", body: application.traction });

  const hasRound = Boolean(d.raise || application.objective);

  const details: Array<{ label: string; value: string }> = [];
  if (d.stageLabel) details.push({ label: "Stage", value: d.stageLabel });
  if (d.country) details.push({ label: "Country", value: d.country });
  if (d.founded) details.push({ label: "Founded", value: d.founded });
  if (d.industries.length > 0)
    details.push({
      label: d.industries.length > 1 ? "Industries" : "Industry",
      value: d.industries.join(", "),
    });
  // Only the sub-industries that add something over the parent names above.
  const extraSubs = d.subIndustries.filter((s) => !d.industries.includes(s));
  if (extraSubs.length > 0)
    details.push({
      label: extraSubs.length > 1 ? "Sub-industries" : "Sub-industry",
      value: extraSubs.join(", "),
    });
  if (d.businessModels.length > 0)
    details.push({ label: "Business model", value: d.businessModels.join(", ") });
  if (d.revenueModels.length > 0)
    details.push({ label: "Revenue model", value: d.revenueModels.join(", ") });
  if (d.source.name) details.push({ label: "Board", value: d.source.name });

  // The anchor sits on a wrapper rather than the first section, which is
  // conditional: a deal with no pitch would otherwise leave the section nav
  // pointing at nothing.
  return (
    <div id={id} className="scroll-mt-32">
      {(hasRound || pitchSections.length > 0) && (
        <DealSection title="The pitch">
          {hasRound && (
            <NarrativeRow label="The round">
              {d.raise && (
                <p className="text-[clamp(1.4rem,1.2rem+0.7vw,1.75rem)] font-semibold leading-tight tracking-[-0.025em]">
                  Raising <span className="tabular-nums">{d.raise}</span>
                  {d.stageLabel && (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      at {d.stageLabel}
                    </span>
                  )}
                </p>
              )}
              {application.objective && (
                <Prose className={d.raise ? "mt-4" : undefined}>
                  {application.objective}
                </Prose>
              )}
            </NarrativeRow>
          )}

          {pitchSections.map((section) => (
            <NarrativeRow key={section.label} label={section.label}>
              <Prose>{section.body}</Prose>
            </NarrativeRow>
          ))}
        </DealSection>
      )}

      {details.length > 0 && (
        <DealSection title="Details">
          <dl className="grid sm:grid-cols-2 sm:gap-x-12">
            {details.map((item) => (
              <div
                key={item.label}
                className="flex items-baseline justify-between gap-6 border-b border-border py-4"
              >
                <dt className="shrink-0 text-[13.5px] text-muted-foreground">
                  {item.label}
                </dt>
                <dd className="min-w-0 text-right text-[14.5px] font-medium leading-snug">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </DealSection>
      )}
    </div>
  );
}

/** One part of the pitch: label left on desktop, above on mobile. */
function NarrativeRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-3 border-b border-border py-7 sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:gap-8">
      <h3 className="pt-1 text-[12px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </h3>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function Prose({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "max-w-[68ch] whitespace-pre-line text-[17px] leading-[1.7] text-foreground/85",
        className
      )}
    >
      {children}
    </p>
  );
}

function SendEmailDialog({
  open,
  application,
  defaultEmail,
  isSending,
  onSend,
  onClose,
}: {
  open: boolean;
  application: Application | null;
  defaultEmail: string;
  isSending: boolean;
  onSend: (email: string) => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  // Seed only when the dialog opens. Depending on defaultEmail too would wipe
  // what the user is typing if Clerk resolves the address late.
  useEffect(() => {
    if (open) setEmail(defaultEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open || !application) return null;
  const company = getCompany(application);
  const name = company?.company_name ?? "this company";
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle>Email deal details</DialogTitle>
        <p className="text-sm text-muted-foreground">
          Send <span className="font-medium text-foreground">{name}</span>'s
          details to:
        </p>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && valid && !isSending) onSend(email.trim());
          }}
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onSend(email.trim())}
            disabled={!valid || isSending}
          >
            {isSending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-1.5 h-4 w-4" />
            )}
            Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const COMMENT_MAX = 2000;

/**
 * The conversation about a deal: header, composer, thread. Lives in the
 * engagement rail on desktop (where the thread scrolls inside the rail) and
 * closes the page on mobile.
 */
function CommentsPanel({
  id,
  applicationId,
  lookbookPublicId,
  origin,
  guest = false,
  scroll = false,
}: {
  /** Anchor for the section nav. */
  id?: string;
  /** Inside the rail: the thread scrolls within the remaining height. */
  scroll?: boolean;
  applicationId: string;
  /** Set on a lookbook profile: the thread there is private to that lookbook. */
  lookbookPublicId?: string;
  /**
   * A signed-out visitor on a lookbook that allows guests: they comment
   * without signing in, under an optional name.
   */
  guest?: boolean;
  /**
   * Which board the reader came in from. Recorded on the comment so the deal
   * profile can tell public-board and global-board comments apart later; the
   * two share one thread, so nothing else can.
   */
  origin: RaiseBoardCommentOrigin;
}) {
  const { isSignedIn } = useUser();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [commentAuthOpen, setCommentAuthOpen] = useState(false);
  const [guestName, setGuestNameState] = useState(getGuestName);
  // Delete is offered for comments posted this session; matching a user's
  // older comments needs their db id on the client (follow-up).
  const [ownIds, setOwnIds] = useState<Set<string>>(new Set());

  // Scope belongs in the key as well as the request — the same deal has a
  // different thread in each lookbook than it does on the public board.
  const key = ["raise-board-comments", applicationId, lookbookPublicId ?? "global"];
  const { data: comments, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => raiseBoardApi.getComments(applicationId, lookbookPublicId),
  });

  const addComment = useMutation({
    mutationFn: (t: string) =>
      guest && lookbookPublicId
        ? raiseBoardApi.addGuestComment(
            applicationId,
            t,
            lookbookPublicId,
            getVisitorId(),
            guestName.trim() || undefined
          )
        : raiseBoardApi.addComment(applicationId, t, lookbookPublicId, origin),
    onSuccess: (res) => {
      setText("");
      // Remembered for their next comment, on this lookbook or another.
      if (guest) setGuestName(guestName);
      // Delete is a signed-in action, so a guest's comment never offers it.
      if (!guest) setOwnIds((prev) => new Set(prev).add(res.comment._id));
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e: unknown) =>
      toast.error(getErrorMessage(e, "Failed to post your comment")),
  });

  const deleteComment = useMutation({
    mutationFn: (id: string) => raiseBoardApi.deleteComment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (e: unknown) =>
      toast.error(getErrorMessage(e, "Failed to delete the comment")),
  });

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    if (!isSignedIn && !guest) {
      setCommentAuthOpen(true);
      return;
    }
    addComment.mutate(t);
  };

  const list: RaiseBoardComment[] = comments ?? [];
  const canPost = isSignedIn || guest;

  return (
    <section
      id={id}
      aria-labelledby="deal-comments-heading"
      className={cn("flex min-h-0 flex-col scroll-mt-32", scroll && "flex-1")}
    >
      <div className={cn("shrink-0", scroll && "px-5 pt-5")}>
        <h2
          id="deal-comments-heading"
          className="text-[15px] font-semibold tracking-[-0.01em]"
        >
          Comments
          {list.length > 0 && (
            <span className="ml-2 text-[12.5px] font-medium tabular-nums text-muted-foreground">
              {list.length}
            </span>
          )}
        </h2>
        {lookbookPublicId && (
          <p className="mt-1 text-[12.5px] text-muted-foreground">
            Only people with this lookbook&rsquo;s link can see this thread.
          </p>
        )}

        {/* Composer. Signed-out visitors can still type; the sign-in dialog
            opens on submit and their draft survives it. */}
        <div className="mt-3 space-y-2">
          {guest && (
            <Input
              value={guestName}
              onChange={(e) => setGuestNameState(e.target.value.slice(0, 60))}
              maxLength={60}
              placeholder="Your name (optional)"
              aria-label="Your name (optional)"
              className="h-9 bg-background text-[13px]"
            />
          )}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, COMMENT_MAX))}
            rows={3}
            maxLength={COMMENT_MAX}
            placeholder={
              canPost
                ? guest
                  ? "Add a comment as a guest"
                  : "Add a comment"
                : "Sign in to leave a comment"
            }
            aria-label="Comment"
            className="block w-full resize-none rounded-lg border border-input bg-background px-3 py-2.5 text-[14px] leading-relaxed outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground focus:border-foreground/30 focus:shadow-[0_0_0_3px_hsl(var(--foreground)/0.05)]"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
            }}
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs tabular-nums text-muted-foreground">
              {text.length}/{COMMENT_MAX}
            </span>
            <Button
              size="sm"
              className="h-8 rounded-lg px-3.5 text-[13px]"
              onClick={submit}
              disabled={addComment.isPending || !text.trim()}
            >
              {addComment.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {canPost ? "Post" : "Sign in to post"}
            </Button>
          </div>
        </div>
      </div>

      <div
        className={cn(
          "mt-5",
          scroll && "min-h-[6rem] flex-1 overflow-y-auto overscroll-contain px-5 pb-5"
        )}
      >
        {isLoading ? (
          <div className="space-y-5">
            {[0, 1].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2 pt-1">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3.5 w-full max-w-md" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            Couldn&rsquo;t load the comments. Refresh to try again.
          </p>
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No comments yet. Start the conversation.
          </p>
        ) : (
          <ul className="divide-y divide-border/80">
            {list.map((c) => {
              const isGuest = c.user === null;
              return (
                <li key={c._id} className="flex gap-3 py-4 first:pt-0">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${
                      isGuest
                        ? "border border-dashed border-border text-muted-foreground"
                        : "bg-secondary text-foreground/70"
                    }`}
                  >
                    {(c.user_name ?? "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold">
                        {c.user_name ?? "Anonymous"}
                      </span>
                      {/* A guest picks their own name, so it is marked as
                          unverified rather than passing for a member's. */}
                      {isGuest && c.user_name !== "Guest" && (
                        <span className="rounded-md border border-border px-1.5 py-px text-[11px] font-medium text-muted-foreground">
                          Guest
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {timeAgo(c.created_on)}
                      </span>
                      {ownIds.has(c._id) && (
                        <button
                          type="button"
                          onClick={() => deleteComment.mutate(c._id)}
                          disabled={deleteComment.isPending}
                          className="ml-auto rounded-md p-1 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                          aria-label="Delete comment"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="mt-1 max-w-[68ch] whitespace-pre-line break-words text-[14px] leading-relaxed text-foreground/85">
                      {c.text}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <BoardAuthDialog
        open={commentAuthOpen}
        onOpenChange={setCommentAuthOpen}
        reason="Sign in to comment on this deal."
      />
    </section>
  );
}

/** Classification tags: the first few, with the rest a click away. */
function TagRun({ tags }: { tags: string[] }) {
  const [open, setOpen] = useState(false);
  const VISIBLE = 6;
  const shown = open ? tags : tags.slice(0, VISIBLE);
  const hidden = tags.length - shown.length;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-1.5">
      {shown.map((t) => (
        <RBTag key={t}>{t}</RBTag>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md px-1.5 py-px text-[12px] font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground"
        >
          +{hidden} more
        </button>
      )}
    </div>
  );
}
